import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { H3, Page, LayoutSingleColumn, Icons, NamedLink } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './PaymentConfirmationPage.module.css';
import { transitions } from '../../transactions/transactionProcessBooking';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { confirmCardPaymentApi } from '../../util/api';
import Spinner from '../../components/IconSpinner/IconSpinner';
import { OMISE_PAYMENT_RESOLUTION, getOmiseChargeResolution } from '../../util/omiseStatus';
import { isScrollingDisabled } from '../../ducks/ui.duck';

export const isVerifiedSuccessfulCharge = (charge, eventStatus) => {
  const resolution = getOmiseChargeResolution(charge);
  const normalizedEventStatus = String(eventStatus || '').toLowerCase();
  const eventDidNotFail = !normalizedEventStatus || normalizedEventStatus === 'success';

  return resolution.resolution === OMISE_PAYMENT_RESOLUTION.CONFIRMED && eventDidNotFail;
};

const CONFIRMATION_POLL_DELAYS_MS = [0, 1500, 2500, 4000, 6000, 8000];

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const clearPaymentConfirmationCookies = () => {
  Cookies.remove('transactionId');
  Cookies.remove('eventStatus');
  Cookies.remove('chargeId');
  Cookies.remove('customOfferId');
  Cookies.remove('customOfferPaymentContext');
  Cookies.remove('confirmTransitionName');
};

export const PaymentConfirmationPageComponent = props => {
  const intl = useIntl();
  const history = useHistory();
  const [paymentConfirmMsg, setPaymentConfirmMsg] = useState('inProgress');
  const { scrollingDisabled } = props;

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const txIdFromCookie = Cookies.get('transactionId');
      const txIdFromQuery = searchParams.get('transactionId') || searchParams.get('txId');
      const eventStatus = Cookies.get('eventStatus') || searchParams.get('eventStatus');
      const chargeId = Cookies.get('chargeId') || searchParams.get('chargeId');
      const customOfferId = Cookies.get('customOfferId');
      const isCustomOfferPaymentContext = Cookies.get('customOfferPaymentContext') === 'true';
      const confirmTransitionName = Cookies.get('confirmTransitionName');
      const transactionId = txIdFromCookie || txIdFromQuery;

      if (!transactionId) {
        console.error('No transaction ID found in cookies or params');
        if (isMounted) setPaymentConfirmMsg('missingTx');
        return;
      }

      const allowedConfirmTransitionNames = [
        transitions.CONFIRM_PAYMENT,
        transitions.CONFIRM_CUSTOM_OFFER_PAYMENT,
        transitions.CONFIRM_PAY_LATER_PAYMENT,
      ];
      const safeConfirmTransitionName = allowedConfirmTransitionNames.includes(
        confirmTransitionName
      )
        ? confirmTransitionName
        : null;
      const successTransitionName =
        safeConfirmTransitionName ||
        (isCustomOfferPaymentContext && transitions.CONFIRM_CUSTOM_OFFER_PAYMENT
          ? transitions.CONFIRM_CUSTOM_OFFER_PAYMENT
          : transitions.CONFIRM_PAYMENT);

      try {
        for (let attempt = 0; attempt < CONFIRMATION_POLL_DELAYS_MS.length; attempt += 1) {
          const delay = CONFIRMATION_POLL_DELAYS_MS[attempt];
          if (delay > 0) {
            await sleep(delay);
          }
          if (!isMounted) return;

          const result = await confirmCardPaymentApi({
            transactionId,
            chargeId,
            eventStatus,
            customOfferId: customOfferId || null,
            isCustomOfferPaymentContext,
            confirmTransitionName: successTransitionName,
          });

          console.info('[payment-confirmation] confirmation status resolved', {
            transactionId,
            chargeId: chargeId || result?.chargeId || null,
            status: result?.status || null,
            omiseChargeStatus: result?.omiseChargeStatus || null,
            paymentStatus: result?.paymentStatus || null,
            currentTransition: result?.currentTransition || null,
            confirmTransitionName: successTransitionName,
            customOfferId: customOfferId || null,
            isCustomOfferPaymentContext,
            completedCustomOfferPayment: result?.completedCustomOfferPayment === true,
            alreadyConfirmed: result?.alreadyConfirmed === true,
            retriedAfterFailure: result?.retriedAfterFailure === true,
            attempt: attempt + 1,
          });

          if (result?.status === OMISE_PAYMENT_RESOLUTION.CONFIRMED) {
            clearPaymentConfirmationCookies();

            if (!isMounted) return;
            setPaymentConfirmMsg('success');
            history.replace(`/order/${transactionId}`);
            return;
          }

          if (result?.status === OMISE_PAYMENT_RESOLUTION.FAILED) {
            clearPaymentConfirmationCookies();

            if (!isMounted) return;
            setPaymentConfirmMsg('expired');
            return;
          }

          if (isMounted) {
            setPaymentConfirmMsg('pending');
          }
        }

        if (isMounted) setPaymentConfirmMsg('pending');
      } catch (err) {
        console.error('confirmPayment failed', err);
        if (isMounted) setPaymentConfirmMsg('error');
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [history]);

  return (
    <Page
      title={intl.formatMessage({ id: 'PaymentConfirmationPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={
          <TopbarContainer
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
        }
        sideNav={null}
        useAccountSettingsNav
        currentPage="PaymentConfirmationPage"
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <div className={css.contentWrapper}>
            {paymentConfirmMsg === 'inProgress' ? (
              <Spinner />
            ) : paymentConfirmMsg === 'success' ? (
              <Icons name="success" />
            ) : paymentConfirmMsg === 'expired' ? (
              <Icons name="failed" />
            ) : paymentConfirmMsg === 'pending' ? (
              <Spinner />
            ) : paymentConfirmMsg === 'error' ? (
              <Icons name="warning" />
            ) : paymentConfirmMsg === 'missingTx' ? (
              <Icons name="sad" />
            ) : null}
            <H3>
              <FormattedMessage
                id={
                  paymentConfirmMsg === 'success'
                    ? 'PaymentConfirmationPage.success'
                    : paymentConfirmMsg === 'expired'
                    ? 'PaymentConfirmationPage.expired'
                    : paymentConfirmMsg === 'pending'
                    ? 'PaymentConfirmationPage.pending'
                    : paymentConfirmMsg === 'error'
                    ? 'PaymentConfirmationPage.error'
                    : paymentConfirmMsg === 'missingTx'
                    ? 'PaymentConfirmationPage.missingTx'
                    : 'PaymentConfirmationPage.paymentTitle'
                }
                defaultMessage={
                  paymentConfirmMsg === 'success'
                    ? 'Payment confirmed. Redirecting to your order…'
                    : paymentConfirmMsg === 'expired'
                    ? 'Payment could not be confirmed (expired/failed).'
                    : paymentConfirmMsg === 'pending'
                    ? 'Payment is still being confirmed. Please wait…'
                    : paymentConfirmMsg === 'error'
                    ? 'Something went wrong confirming your payment.'
                    : paymentConfirmMsg === 'missingTx'
                    ? 'We could not find your transaction.'
                    : 'Please wait while we confirm your payment…'
                }
              />
            </H3>
            {paymentConfirmMsg !== 'inProgress' && paymentConfirmMsg !== 'pending' && (
              <NamedLink name="SearchPage">Continue Searching</NamedLink>
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const PaymentConfirmationPage = compose(
  connect(mapStateToProps)
)(PaymentConfirmationPageComponent);

export default PaymentConfirmationPage;
